import { Action } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ArcGisToken,
  SearchItem, DnvLatLng, DnvMapState, LayerState, DnvLegendState, BaseMapState, DnvSearchState, DnvFeatureLayer,
  SearchItemType, MeasurementUnit, MeasurementUnits,
  PropertyInfo,
  DnvDrawingInfo,
  DnvSymbol,
  DnvSymbology
} from 'dnv-lib';
import { NgxGalleryOptions, NgxGalleryAnimation, NgxGalleryImageSize } from '@vinlos/ngx-gallery';
import { SearchedInfo } from '../../models/search-info';


// tslint:disable-next-line: typedef
export function prop<T, K extends keyof T>(obj: T, key: K) {
  return obj[key];
}

export interface Tracking {
  application: string;
  action: string;
  target: string;
  details: string;
  stacktrace: string;
}

export interface ApplicationState {
  access: boolean;
  status: ApplicationStatus;
  windowInnerHeight: number;
  windowInnerWidth: number;
  galleryOptions: NgxGalleryOptions[];
  redirectPath?: string;
  gridSelectLayer: DnvFeatureLayer;
  gridSelectLayerCache: Map<string, GridPayload>;
  gridRasterHighlight: any;
  featureRasterHighlight: any;
  kvSections: KvSection[];
  propertyInfo: PropertyInfo;
  arcGisToken: ArcGisToken;
  cctvInspections: SelectedFeatureInspection[];
  selectedCctvInspection: SelectedFeatureInspection;
  toolboxStatus: ToolboxStatus;
  cctvNotes: any;
  toolboxMailMergeState: ToolboxMailMergeState;
  mailMergeDBLinks: string;
  mailMergeList: any;
  search: SearchPayload;
  streetView: boolean;
  measurements: any;
  editFeature: { featureType: string, featureLayerId: number, shape: string };
  editPermission: { editableLayers: EditPermissionPayload[], toolname: string }[];
  newFeatureLatLng: DnvLatLng[];
  lastEditFeature: number;
  maxAsset: { assetId: string, name: string };
  searchSpinner: boolean;
  spinner: boolean;
  userProfile: UserProfile;
  editFeatureFields: any;
  appSettings: { [key: string]: string };
  relatedRecords: any;
  selectedFeatureAttachments: any;
  editRequiredFields: requiredFields[];
  editReadOnlyFields: readOnlyFields[];
  unitsLinear: MeasurementUnit[];
  unitsArea: MeasurementUnit[];
  externalFeature: ExternalFeatureType;
  alertHidden: boolean;
  editFeatureShowActiveLayer: boolean;
  searchAsset: { featureLayerId: number, assetId: string };
  cad: EngDrawReport[];
  outstandingED: any;
  domainDictionary: any;
  shapeCreated: boolean;
  toolsPermissions: ToolsPermissions[];
  populationState: PopulationState;
  fromMeasureTool: boolean;
}

export interface ExternalFeatureType {
  message: string;
  pending: boolean;
}

export interface EngDrawReport {
  adminVoid: any;
  capProjId: string;
  comments: string;
  designer: string;
  drawing: string;
  format: string;
  id: number;
  projectDescription: string;
  projectsFrom: string;
  projectName: string;
  projectStartDate: Date;
  projectStatus: string;
  projectStreet: string;
  projectTo: string;
  deliveryLead: string;
}

export interface OutstandingED {
  adminDrawing: string;
  admin_MustHaveAsbuilt: string;
  admin_ReceivedASB: string;
  admin_ReceivedCON: string;
  comments: string;
  designer: string;
  projectStartDate: Date;
  status: string;
}

export enum ApplicationStatus {
  Error, // 0
  // Live,
  Legend, // 1
  Layer, // 2
  Table, // 3
  Toolbox, // 4
  Draw, // 5
  Measure, // 6
  Idle, // 7
  Export, // 8
}

export enum ToolboxStatus {
  Idle, // 0
  PropertyInfo, // 1
  EngDrawing, // 2
  CCTV, // 3
  ServiceConnections, // 4
  MailMerge, // 5
  HistoricalAerials, // 6
  EditFeatures, // 7
  PostingPlans, // 8
  AssetFinder, // 9
  WMRP, // 10
  PopulationCalculator // 11
}

export enum DynamicFeatureLayer {
  WmrpAnalysis,
  WmrpSchedule,
  WmrpPoorAccess,
  WmrpMergedScores
}

// This assumes that there can be a maximum of 10000 feature layers in the system
export function dynamicFeatureLayerIdGenerator(dynamicFeatureLayer: DynamicFeatureLayer, index: number, featureLayerId: number) {
  return (1000 * (dynamicFeatureLayer + 1) + index) * 10000 + (featureLayerId % 10000);
}
export function featureLayerIdFromDynamicFLId(dynamicFeatureLayerId: number) {
  return dynamicFeatureLayerId % 10000;
}

export interface UserNotification {
  type: string; // One of: 'success', 'error', 'info', 'warning'
  title: string;
  message: string;
  options: { [id: string]: any };
}

export interface EngineeringDrawingDetail {
  date: string;
  number: string;
  type: string;
  includedInfrastructure: string;
  sensitive: string;
  url: string;
  projectName: string;
}

export interface PostingPlanDetail {
  drawNumber: string;
  filename: string;
  url: string;
}

// export interface AppState {
//   mapState: DnvMapState;
//   layerState: LayerState;
//   legendState: DnvLegendState;
//   baseMapState: BaseMapState;
//   dnvSearchState: DnvSearchState;
//   // mapViewState: MapViewState;
// }


export const initialArcGisToken: ArcGisToken = {
  token: '',
  expires: 0,
  ssl: true
};

export function ArcGisTokenAboutToExpire(arcGisToken: ArcGisToken): boolean {
  // console.warn(arcGisToken.expires, (arcGisToken.expires < (Date.now() + (30 * 1000))));
  return arcGisToken.expires < (Date.now() + (30 * 1000)); // 5 * 60 * 1000
}

export const initialApplicationState: ApplicationState = {
  status: ApplicationStatus.Idle,
  access: true,
  galleryOptions: [
    {
      width: '100%',
      height: '300px',
      imageArrowsAutoHide: true,
      thumbnails: false,
      imageArrows: false,
      thumbnailsColumns: 4,
      previewCloseOnEsc: true,
      imageAnimation: NgxGalleryAnimation.Slide
    },
    { breakpoint: 800, width: '100%', height: '300px', thumbnailsColumns: 3 },
    { breakpoint: 300, width: '100%', height: '200px', thumbnailsColumns: 2 },
    { arrowPrevIcon: 'fas fa-chevron-left', arrowNextIcon: 'fas fa-chevron-right' }
  ],
  windowInnerHeight: window.innerHeight,
  windowInnerWidth: window.innerWidth,
  gridSelectLayer: null,
  gridSelectLayerCache: new Map<string, GridPayload>(),
  gridRasterHighlight: {},
  featureRasterHighlight: {},
  kvSections: null,
  propertyInfo: {
    Properties: [],
    PropertyCount: 0
  },
  arcGisToken: initialArcGisToken,
  cctvInspections: [],
  selectedCctvInspection: null,
  toolboxStatus: ToolboxStatus.Idle,
  cctvNotes: {
    cctvData: [],
    galleryOptions: [],
    objectId: ''
  },
  toolboxMailMergeState: {
    selectionMethod: 'property',
    selectionBuffer: 0,
    area: {},
    bufferedArea: {},
    selectedParcels: [],
    isOwners: false,
    isOccupants: false,
    groupOwners: false,
    prefOwn: false,
    resultCount: 0
  },
  mailMergeDBLinks: '',
  mailMergeList: [],
  search: null,
  streetView: false,
  measurements: null,
  editFeature: { featureType: '', featureLayerId: 0, shape: '' },
  editPermission: null,
  newFeatureLatLng: null,
  lastEditFeature: null,
  maxAsset: null,
  searchSpinner: false,
  spinner: false,
  userProfile: null,
  editFeatureFields: null,
  appSettings: null,
  relatedRecords: null,
  selectedFeatureAttachments: null,
  editRequiredFields: [],
  editReadOnlyFields: [],
  unitsLinear: MeasurementUnits.LinearUnits,
  unitsArea: MeasurementUnits.AreaUnits,
  externalFeature: { message: '', pending: false },
  alertHidden: true,
  editFeatureShowActiveLayer: false,
  searchAsset: { featureLayerId: null, assetId: null },
  cad: null,
  outstandingED: null,
  domainDictionary: [],
  shapeCreated: false,
  toolsPermissions: null,
  populationState: {
    selectionMethod: '',
    selectedArea: {},
    buildings: []
  },
  fromMeasureTool: false
};

export interface LocationCoordinate {
  ltopid: string;
  dblink: string;
  lat: number;
  lng: number;
}

export interface SearchLocation {
  locationType: string;
  location: string;
}

export interface SearchCoordinatesPayload {
  id: number;
  name: string;
  type: SearchItemType;
  searchLocation?: SearchLocation[];
  assetId?: string;
  updatedBy?: string;
  success?: LocationCoordinate;
  error?: HttpErrorResponse;
}

export interface GridSelectLayerPayload {
  dnvFeatureLayer: DnvFeatureLayer;
  success?: any;
  error?: HttpErrorResponse;
}

export interface SearchPayload {
  search: SearchedInfo;
  success?: any;
  error?: HttpErrorResponse;
}


export interface SearchFromCoordinatesErrorPayload {
  error: string;
  locationDescription: string[];
}

export interface GridPayload {
  fieldAliases: object[];
  data: object[];
  geometry: object[];
  objectid: string;
  objectidclean: string;
}

export interface KvSection {
  title: string;
  kvPair: Array<[string, string]>;
}

export interface RenewArcGisTokenPayload {
  delayedActions: Action[];
  success?: ArcGisToken;
  error?: HttpErrorResponse;
}

export interface SelectedFeatureInspection {
  objectId: string;
  attrs: Array<[string, string]>;
  galleryOptions: any;
  attachment: any;
  inspType: string;
  video: string;
}

export interface ToolboxMailMergeState {
  selectionMethod: string;
  selectionBuffer: number;
  area: any;
  bufferedArea: any;
  selectedParcels: any[];
  isOwners: boolean;
  isOccupants: boolean;
  groupOwners: boolean;
  prefOwn: boolean;
  resultCount: number
}

export interface ToolboxMailMergeOptions {
  isOwners: boolean;
  isOccupants: boolean;
  groupOwners: boolean;
  prefOwn: boolean;
}

export interface ToolboxMailMergePayload {
  toolboxMailMergeState: ToolboxMailMergeState;
  invalidate: boolean;
  success?: any;
  error?: HttpErrorResponse;
}

export interface UserProfile {
  givenName: string;
  surname: string;
  userPrincipalName: string;
}

export interface EditPermissionPayload {
  add: boolean;
  attributes: boolean;
  autoIncrementField: string;
  bulk: boolean;
  delete: boolean;
  featureLayerId: number;
  geometry: boolean;
  layerId: number;
  shape: string;
  snapLayers: { layerId: number, featureLayerId: number }[];
  url: string;
  write_Url: string;
  featureLayerName: string;
  drawingInfo?: DnvDrawingInfo;
  symbology?: DnvSymbology;
  roleName: string;
};

export interface ToolsPermissions {
  toolName: string;
  allowed: boolean;
  roleName: string;
}

export interface AttachmentPayload {
  url: string;
  objectId: number;
  relatedTableId: number;
}

export interface AttachmentsPayload {
  url: string;
  objectIds: number[];
  relatedTableId: number;
}

export interface requiredFields {
  editFeatureLayerId: number,
  requiredField: string,
  featureLayerId: number
}

export interface readOnlyFields {
  editFeatureLayerId: number,
  readOnlyField: string,
  featureLayerId: number
}

export interface PopulationState {
  selectionMethod: string;
  selectedArea: any;
  buildings: { assetId: string, population: number }[];
}

export interface IdentifySurroundingFeatureParam {
  latlng: DnvLatLng;
  northWestBound: DnvLatLng;
  southEastBound: DnvLatLng;
  imageDisplay: string;
  layerNum: string;
  isEditTool?: boolean;
  whereClause: string;
  url?: string;
}